import React, { useState, useMemo, useCallback } from 'react';
import { X, Plus, Trash2, Search, CheckSquare, Square, Loader2, FolderOpen } from 'lucide-react';
import { useAppSelector } from '../../redux/hooks';
import {
  useListPortfoliosQuery,
  useCreatePortfolioMutation,
  useUpdatePortfolioMutation,
  useDeletePortfolioMutation,
  useListAdminSitesQuery,
  useLazyListPortfolioSitesQuery,
} from '../../redux/api/portfolioApi';
import type { Portfolio, PortfolioSite } from '../../redux/api/portfolioApi';

interface PortfolioManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPortfolioId: string | null;
  onSelectPortfolio: (id: string | null) => void;
}

const PortfolioManagerModal: React.FC<PortfolioManagerModalProps> = ({
  isOpen,
  onClose,
  selectedPortfolioId,
  onSelectPortfolio,
}) => {
  const profile = useAppSelector((state) => state.auth.profile);
  const tenantId = (profile as any).active_tenant?.tenant_id || (profile as any).tenant_id || '';

  const { data: portfoliosData, refetch: refetchPortfolios } = useListPortfoliosQuery(undefined);
  const portfolios = portfoliosData?.items || [];

  const { data: adminSitesData } = useListAdminSitesQuery({ limit: 100 });
  const allSites: PortfolioSite[] = (adminSitesData as any)?.items || [];

  const [createPortfolio, { isLoading: isCreating }] = useCreatePortfolioMutation();
  const [updatePortfolio] = useUpdatePortfolioMutation();
  const [deletePortfolio, { isLoading: isDeleting }] = useDeletePortfolioMutation();
  const [fetchPortfolioSites] = useLazyListPortfolioSitesQuery();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [portfolioSiteIds, setPortfolioSiteIds] = useState<Set<string>>(new Set());
  const [siteSearch, setSiteSearch] = useState('');
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [newName, setNewName] = useState('');

  const editingPortfolio = portfolios.find(p => p.id === editingId);

  const loadPortfolioSites = useCallback(async (portfolioId: string) => {
    setIsLoadingSites(true);
    try {
      const resp = await fetchPortfolioSites({ portfolio_id: portfolioId }).unwrap();
      const sites = (resp as any)?.items || (Array.isArray(resp) ? resp : []);
      setPortfolioSiteIds(new Set(sites.map((s: PortfolioSite) => s.site_id || s.id || '')));
    } catch {
      setPortfolioSiteIds(new Set());
    }
    setIsLoadingSites(false);
  }, [fetchPortfolioSites]);

  const handleSelectPortfolio = useCallback((portfolio: Portfolio) => {
    setEditingId(portfolio.id);
    setEditName(portfolio.name);
    setEditDescription(portfolio.description || '');
    setSiteSearch('');
    loadPortfolioSites(portfolio.id);
  }, [loadPortfolioSites]);

  const handleCreate = useCallback(async () => {
    if (!newName.trim() || !tenantId) return;
    try {
      const created = await createPortfolio({ name: newName.trim(), tenant_id: tenantId }).unwrap();
      setNewName('');
      refetchPortfolios();
      handleSelectPortfolio(created);
    } catch (err) {
      console.error('Failed to create portfolio', err);
    }
  }, [newName, tenantId, createPortfolio, refetchPortfolios, handleSelectPortfolio]);

  const handleDelete = useCallback(async () => {
    if (!editingId) return;
    try {
      await deletePortfolio(editingId).unwrap();
      if (selectedPortfolioId === editingId) {
        onSelectPortfolio(null);
      }
      setEditingId(null);
      refetchPortfolios();
    } catch (err) {
      console.error('Failed to delete portfolio', err);
    }
  }, [editingId, deletePortfolio, selectedPortfolioId, onSelectPortfolio, refetchPortfolios]);

  const handleSaveName = useCallback(async () => {
    if (!editingId || !editName.trim()) return;
    try {
      await updatePortfolio({
        portfolio_id: editingId,
        body: { name: editName.trim(), description: editDescription.trim() || undefined },
      }).unwrap();
      refetchPortfolios();
    } catch (err) {
      console.error('Failed to update portfolio', err);
    }
  }, [editingId, editName, editDescription, updatePortfolio, refetchPortfolios]);

  const filteredSites = useMemo(() => {
    if (!siteSearch) return allSites;
    const lower = siteSearch.toLowerCase();
    return allSites.filter(s =>
      (s.site_name || s.name || '').toLowerCase().includes(lower)
    );
  }, [allSites, siteSearch]);

  if (!isOpen) return null;

  return (
    <div className="eg-modal-backdrop" onClick={onClose}>
      <div
        className="eg-modal-lg"
        style={{ maxWidth: '700px', width: '90vw' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Manage Portfolios
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-[var(--color-surface-hover)] transition-colors"
          >
            <X className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
          </button>
        </div>

        <div className="flex gap-4" style={{ minHeight: '400px' }}>
          {/* Left: Portfolio list */}
          <div className="w-56 shrink-0 flex flex-col">
            {/* Create new */}
            <div className="flex gap-1.5 mb-3">
              <input
                type="text"
                placeholder="New portfolio name..."
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreate()}
                className="eg-input flex-1 text-xs"
              />
              <button
                onClick={handleCreate}
                disabled={!newName.trim() || isCreating}
                className="eg-btn-primary px-2 py-1 text-xs disabled:opacity-50"
              >
                {isCreating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              </button>
            </div>

            {/* Portfolio list */}
            <div
              className="flex-1 overflow-y-auto space-y-0.5 rounded-lg"
              style={{ border: '1px solid var(--color-border)' }}
            >
              {portfolios.length === 0 ? (
                <div className="flex flex-col items-center py-8" style={{ color: 'var(--color-text-muted)' }}>
                  <FolderOpen className="w-8 h-8 mb-2 opacity-30" />
                  <p className="text-xs">No portfolios</p>
                </div>
              ) : (
                portfolios.map(p => (
                  <button
                    key={p.id}
                    onClick={() => handleSelectPortfolio(p)}
                    className="w-full text-left px-3 py-2 text-xs transition-colors"
                    style={{
                      background: editingId === p.id ? 'rgba(23, 196, 128, 0.1)' : 'transparent',
                      color: editingId === p.id ? '#17c480' : 'var(--color-text-primary)',
                    }}
                  >
                    <div className="font-medium truncate">{p.name}</div>
                    {selectedPortfolioId === p.id && (
                      <span className="text-[10px]" style={{ color: '#17c480' }}>Active</span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Right: Detail panel */}
          <div className="flex-1 min-w-0">
            {editingPortfolio ? (
              <div className="space-y-4">
                {/* Name & description */}
                <div>
                  <label className="text-xs font-medium block mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Portfolio Name
                  </label>
                  <input
                    type="text"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    onBlur={handleSaveName}
                    className="eg-input w-full text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Description
                  </label>
                  <input
                    type="text"
                    value={editDescription}
                    onChange={e => setEditDescription(e.target.value)}
                    onBlur={handleSaveName}
                    placeholder="Optional description..."
                    className="eg-input w-full text-sm"
                  />
                </div>

                {/* Actions row */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { onSelectPortfolio(editingPortfolio.id); onClose(); }}
                    className="eg-btn-primary text-xs px-3 py-1.5"
                  >
                    Set as Active
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="eg-btn-danger text-xs px-3 py-1.5 flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" />
                    Delete
                  </button>
                </div>

                {/* Sites in portfolio */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                      Sites ({portfolioSiteIds.size})
                    </label>
                  </div>

                  <div className="relative mb-2">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--color-text-muted)' }} />
                    <input
                      type="text"
                      placeholder="Search sites..."
                      value={siteSearch}
                      onChange={e => setSiteSearch(e.target.value)}
                      className="eg-input w-full text-xs pl-8"
                    />
                  </div>

                  <div
                    className="max-h-48 overflow-y-auto rounded-lg"
                    style={{ border: '1px solid var(--color-border)' }}
                  >
                    {isLoadingSites ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="w-4 h-4 animate-spin text-engramiq-green" />
                      </div>
                    ) : (
                      filteredSites.map(site => {
                        const id = site.site_id || site.id || '';
                        const isIn = portfolioSiteIds.has(id);
                        return (
                          <div
                            key={id}
                            className="flex items-center gap-2 px-2.5 py-1.5 text-xs transition-colors hover:bg-[var(--color-surface-hover)]"
                          >
                            {isIn ? (
                              <CheckSquare className="w-3.5 h-3.5 text-[#17c480] shrink-0" />
                            ) : (
                              <Square className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--color-text-muted)' }} />
                            )}
                            <span className="truncate" style={{ color: 'var(--color-text-primary)' }}>
                              {site.site_name || site.name}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full" style={{ color: 'var(--color-text-muted)' }}>
                <FolderOpen className="w-10 h-10 mb-3 opacity-20" />
                <p className="text-sm">Select a portfolio to view details</p>
                <p className="text-xs mt-1">Or create a new one</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioManagerModal;
